import { isReplyUnderFeedback } from '~/composables/domain/activity/isReplyUnderFeedback';

/**
 * activityList.do / selectPlan.activityList → ActivityCard(PlanActivityPanel) 형식
 * useCollaborationStore.handleFetchActivityList 와 동일 분류·정렬 규칙
 */
export function mapActivityListForCards(activityList: unknown[] = []) {
  const activityItems: Record<string, unknown>[] = [];
  const feedbackMap = new Map<string | number, Record<string, unknown>[]>();
  const feedbackReplyMap = new Map<string | number, Record<string, unknown>[]>();

  (activityList || []).forEach((activity: Record<string, unknown>) => {
    const activityItem: Record<string, unknown> = {
      id: activity.activityNo,
      status: activity.resultStatusNm,
      evaluation: activity.statusNm,
      currentDate: activity.activityDt,
      activityDt: activity.activityDt,
      createdDate: activity.createDt,
      createUserNm: activity.createUserNm || activity.userNm,
      createUserDept: activity.createUserDept || activity.userDeptNm || '',
      readUserCnt: activity.readUserCnt ?? 0,
      readUserStr: activity.readUserStr ?? '',
      activityUnreadCnt: activity.activityUnreadCnt ?? 0,
      content: activity.content,
      replyCnt: activity.replyCnt ?? 0,
      feedbackList: [],
      createUserId: activity.createUserId,
      statusCd: activity.statusCd,
      statusNm: activity.statusNm,
      // selectKrPlan activityDatas 는 statusColor 대신 color
      statusColor: activity.statusColor || activity.color,
      resultStatusCd: activity.resultStatusCd,
      resultStatusNm: activity.resultStatusNm,
      planId: activity.planId,
      atchFileId: activity.atchFileId,
      atchFileCnt: activity.atchFileCnt,
      imgPaths: (activity.imgPaths as string) || '',
      thumbPaths: (activity.thumbPaths as string) || '',
      ibmsActivityId: activity.ibmsActivityId ?? activity.IBMS_ACTIVITY_ID ?? '',
    };
    if (activity.statusCd || activity.resultStatusCd) {
      activityItems.push(activityItem);
    } else if (activity.upReplyNo) {
      const key = activity.activityNo as string | number;
      if (!feedbackReplyMap.has(key)) feedbackReplyMap.set(key, []);
      feedbackReplyMap.get(key)!.push({
        id: activity.activityNo,
        content: activity.content,
        createdDate: activity.createDt,
        createUserNm: activity.createUserNm || activity.userNm,
        createUserDept: activity.createUserDept || activity.userDeptNm || '',
        readUserCnt: activity.readUserCnt ?? 0,
        readUserStr: activity.readUserStr ?? '',
        activityUnreadCnt: activity.activityUnreadCnt ?? 0,
        createUserId: activity.createUserId,
        replyNo: activity.replyNo,
        upReplyNo: activity.upReplyNo,
        planId: activity.planId,
        replyCnt: activity.replyCnt ?? 0,
        atchFileId: activity.atchFileId,
        atchFileCnt: activity.atchFileCnt,
        upContent: (activity.upContent as string) || '',
        upUserId: activity.upUserId || '',
        upUserNm: (activity.upUserNm as string) || '',
        upUserDept: (activity.upUserDept as string) || '',
        upCreatedDate: (activity.upCreateDt as string) || '',
      });
    } else {
      const key = activity.activityNo as string | number;
      if (!feedbackMap.has(key)) feedbackMap.set(key, []);
      feedbackMap.get(key)!.push({
        id: activity.activityNo,
        content: activity.content,
        createdDate: activity.createDt,
        createUserNm: activity.createUserNm || activity.userNm,
        createUserDept: activity.createUserDept || activity.userDeptNm || '',
        readUserCnt: activity.readUserCnt ?? 0,
        readUserStr: activity.readUserStr ?? '',
        activityUnreadCnt: activity.activityUnreadCnt ?? 0,
        createUserId: activity.createUserId,
        replyNo: activity.replyNo,
        planId: activity.planId,
        replyCnt: activity.replyCnt ?? 0,
        atchFileId: activity.atchFileId,
        atchFileCnt: activity.atchFileCnt,
        feedbackReplyList: [],
      });
    }
  });

  activityItems.forEach((activity: Record<string, unknown>) => {
    const actId = activity.id as string | number;
    if (!feedbackMap.has(actId)) return;

    const sortedFeedback = feedbackMap.get(actId)!.sort((a, b) => {
      const dateA = new Date(String(a.createdDate));
      const dateB = new Date(String(b.createdDate));
      return dateA.getTime() - dateB.getTime();
    });

    sortedFeedback.forEach((feedback: Record<string, unknown>) => {
      if (feedbackReplyMap.has(actId)) {
        const allReplies = feedbackReplyMap.get(actId)!;
        const filteredReplies = allReplies.filter((reply: Record<string, unknown>) =>
          isReplyUnderFeedback(reply, feedback.replyNo as string | number, allReplies),
        );

        const sortedReplies = filteredReplies.sort(
          (a, b) => Number(a.replyNo) - Number(b.replyNo),
        );

        sortedReplies.forEach((reply: Record<string, unknown>) => {
          if (feedback.replyNo === reply.upReplyNo) {
            reply.upContent = reply.upContent || feedback.content;
            reply.upUserId = reply.upUserId || feedback.createUserId;
            reply.upUserNm = reply.upUserNm || feedback.createUserNm;
            reply.upUserDept = reply.upUserDept || feedback.createUserDept;
            reply.upCreatedDate = reply.upCreatedDate || feedback.createdDate;
          } else {
            const matchedReply = sortedReplies.find(
              (r: Record<string, unknown>) => r.replyNo === reply.upReplyNo,
            );
            if (matchedReply) {
              reply.upContent = reply.upContent || matchedReply.content;
              reply.upUserId = reply.upUserId || matchedReply.createUserId;
              reply.upUserNm = reply.upUserNm || matchedReply.createUserNm;
              reply.upUserDept = reply.upUserDept || matchedReply.createUserDept;
              reply.upCreatedDate = reply.upCreatedDate || matchedReply.createdDate;
            } else {
              reply.upContent = reply.upContent || feedback.content;
              reply.upUserId = reply.upUserId || feedback.createUserId;
              reply.upUserNm = reply.upUserNm || feedback.createUserNm;
              reply.upUserDept = reply.upUserDept || feedback.createUserDept;
              reply.upCreatedDate = reply.upCreatedDate || feedback.createdDate;
            }
          }
        });

        feedback.feedbackReplyList = sortedReplies;
      }
    });

    activity.feedbackList = sortedFeedback;
  });

  return activityItems;
}
